// useInlinePetEmbed — React hook that mounts the native 3D pet
// preview (BoodaInlinePetView, iOS-only) as an overlay above the
// WebView at the position of a DOM placeholder.
//
// Why a native overlay and not a web canvas: the kid pet is a USDZ
// file with embedded animations. Rendering USDZ inline on the web
// requires GLB conversion + a WebGL viewer (model-viewer / three),
// adding a build pipeline + bigger bundles. Mounting the existing
// RealityKit ARView at a rect over the WebView reuses the loader,
// animation, and lighting code we already have for AR mode — zero
// extra Three.js dependencies, native 60fps GPU rendering.
//
// Usage:
//   const ref = useRef(null);
//   useInlinePetEmbed({ ref, filename: "/img/Alien/alien_idle.usdz" });
//   return <div ref={ref} style={{ width: 280, height: 280 }} />;
//
// The hook is a no-op off iOS (returns silently), so call sites
// don't need a platform check — they just supply a sized div and
// the PNG fallback shows where the native view would land.

import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { BoodaArQuickLook } from "./boodaArQuickLook";

function isIOSNative() {
  if (typeof window === "undefined") return false;
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

function readRect(el) {
  if (!el || typeof el.getBoundingClientRect !== "function") return null;
  const r = el.getBoundingClientRect();
  // CSS pixels === UIKit points on iOS (no devicePixelRatio scaling
  // needed). Round to avoid sub-pixel jitter when CALayer snaps.
  return {
    x:      Math.round(r.left),
    y:      Math.round(r.top),
    width:  Math.round(r.width),
    height: Math.round(r.height),
  };
}

function rectsEqual(a, b) {
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

export function useInlinePetEmbed({ ref, filename, onTap }) {
  const embedIdRef = useRef(null);
  const lastRectRef = useRef(null);
  const onTapRef = useRef(onTap);
  onTapRef.current = onTap;

  // Subscribe to "petTapped" events from the native view so a tap
  // on the 3D overlay still runs the React onTap (mood pulse, sfx)
  // — without this, the native UIView eats the tap and the
  // underlying button never sees it.
  useEffect(() => {
    if (!isIOSNative()) return;
    const sub = BoodaArQuickLook.addPetTappedListener((evt) => {
      if (!evt || !evt.embedId) return;
      if (evt.embedId !== embedIdRef.current) return;
      try { onTapRef.current && onTapRef.current(); } catch { /* noop */ }
    });
    return () => {
      try {
        if (!sub) return;
        if (typeof sub.then === "function") {
          sub.then((h) => h?.remove?.()).catch(() => {});
        } else {
          sub.remove?.();
        }
      } catch { /* noop */ }
    };
  }, []);

  // Mount / dismount the native embed.
  useEffect(() => {
    if (!isIOSNative() || !filename) return;
    const el = ref?.current;
    if (!el) return;

    let cancelled = false;
    const rect = readRect(el);
    if (!rect || rect.width <= 0 || rect.height <= 0) {
      // DOM laid out 0x0 — wait one frame and retry.
      const id = requestAnimationFrame(() => {
        if (cancelled) return;
        const r2 = readRect(el);
        if (!r2 || r2.width <= 0 || r2.height <= 0) return;
        lastRectRef.current = r2;
        BoodaArQuickLook.embedPreview({ filename, rect: r2 })
          .then((res) => {
            if (cancelled) {
              if (res?.embedId) {
                BoodaArQuickLook.dismissEmbed({ embedId: res.embedId }).catch(() => {});
              }
              return;
            }
            embedIdRef.current = res?.embedId || null;
          })
          .catch((e) => console.warn("[inline-pet] embedPreview failed:", e?.message || e));
      });
      return () => { cancelled = true; cancelAnimationFrame(id); };
    }
    lastRectRef.current = rect;
    BoodaArQuickLook.embedPreview({ filename, rect })
      .then((res) => {
        if (cancelled) {
          if (res?.embedId) {
            BoodaArQuickLook.dismissEmbed({ embedId: res.embedId }).catch(() => {});
          }
          return;
        }
        embedIdRef.current = res?.embedId || null;
      })
      .catch((e) => console.warn("[inline-pet] embedPreview failed:", e?.message || e));

    return () => {
      cancelled = true;
      const id = embedIdRef.current;
      embedIdRef.current = null;
      if (id) {
        BoodaArQuickLook.dismissEmbed({ embedId: id }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename]);

  // Keep the native rect in sync as the DOM scrolls / resizes /
  // tab-switches / keyboard pushes layout.
  useEffect(() => {
    if (!isIOSNative()) return;
    const el = ref?.current;
    if (!el) return;

    const syncRect = () => {
      const id = embedIdRef.current;
      if (!id) return;
      const r = readRect(el);
      if (!r || r.width <= 0 || r.height <= 0) return;
      if (rectsEqual(r, lastRectRef.current)) return;
      lastRectRef.current = r;
      BoodaArQuickLook.updateEmbedRect({ embedId: id, rect: r }).catch(() => {});
    };

    // Poll once per frame while mounted. RAF is cheap (16ms) and
    // catches every layout pathway — scroll, resize, tab switch,
    // keyboard, css transitions — without us having to hook each
    // one individually. We early-out via rectsEqual when nothing
    // changed, so the actual native call is rare.
    let rafId = 0;
    const tick = () => {
      syncRect();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useInlinePetEmbed;
